/**
 * goodDirection convention:
 *   - 'up'      : higher is better (cash, deals, conversions)
 *   - 'down'    : lower is better (cancellations, no-shows, orphan rate, losses)
 *   - 'neutral' : no good/bad signal (rescheduled, installments_count — volume only)
 *
 * Used by /admin/stoplights to decide green/amber/red on weekly direction signal.
 * Also forms the SSOT for ChangeBadge color logic going forward (currently hardcoded "positive=green").
 * See docs/PAGE_stoplights_weekly_snapshot_direction_signal.md.
 */

// =============================================================================
// Metric Metadata - Formula Definitions for 40+ KPIs
// =============================================================================
// PURPOSE: Documents the calculation logic, database columns, and filters for every
// metric displayed in the dashboard. Powers interactive metric tooltips.
//
// ARCHITECTURE:
// - Each metric has: formula (plain English), columns (DB refs), filterDescription
// - Optional chatExamples for DM metrics (shows real conversation snippets)
// - Used by MetricTooltip component for on-hover documentation
//
// KEY DECISIONS:
// - Single source of truth: all formulas documented here (not scattered in code)
// - Plain English formulas: readable by non-technical users
// - Column references: enables future query optimization audits
// - Chat examples: visual learning for complex DM metrics (new DMs vs conversations)
// =============================================================================
// CHANGELOG
// 2026-04-26 (WK17 review):
//   - engaged: dropped lead-msg requirement, now "human setter sent ≥1 msg"
//     (ManyChat excluded). Paired UI label update in DMHealthTab.tsx.
//   - cancellations: added lifetime no-SCO check (lead must have NO SCO ever
//     for the cancel event to count).
//   - closerBookedCalls: rewritten — count of call_booked events for leads
//     who also have a sales_pre_call. Closer attribution = SCO closer_id when
//     SCO exists, else call_booked.closer_id (cancelled subset includes all
//     failure modes: host-cancelled, lead-cancelled, neglected).
//   - outboundAttempts → renamed to outboundNewDMs (still includes ManyChat).
//     [outboundNewDMs later DELETED 2026-08-18, R62 — zero signal, GHL 1:1.]
//   - inboundNewDMs: NEW metric (lead-first NewDMs). Math:
//     inboundNewDMs + (setter-first complement) = newDMChats.
//   - installmentsCollected / installmentsCount: LIKE → exact match
//     (paying_installment is the only status with "installment").
//   - cashPerCallRaw: DELETED (Calendly-noise denominator, no consumers).
//   - preCallsMade: marked RETIRED — use callsBooked. Apr 6 ruling collapsed
//     them. 21 codebase refs in useOperationsAnalytics.ts pending migration.
//   - funnel.new → renamed to funnel.newWebinar.
//   - funnel.registered → renamed to funnel.webinar.
//   - funnel.booked: kept on raw call_booked. Future funnel.preCall metric
//     planned for canonical (sales_pre_call) per-source bookings.
//   - followUps: redefined — every setter msg in a sequence where each
//     consecutive setter msg is 6h+ apart with no lead reply between counts
//     as 1 follow-up. Tagged inaccurate (definition documented, not yet
//     implemented in pipeline).
//   - AOV: tagged inaccurate-deferred. paying_installment in numerator
//     without proper denominator skews high. Corrected formula in note.
//   - cashCollected + revenueClosed: tagged inaccurate-deferred. Combined
//     high-ticket + low-ticket distorts averages. Use highTicket.* /
//     lowTicket.* siblings for clean splits.
// 2026-04-27 (Global ManyChat exclusion rule for rate metrics):
//   - Rate metrics on DM Analytics (ghostRate, conversationRate, responseRate when
//     unparked) use human-setter scope on both numerator and denominator. Volume
//     metrics (newDMChats, manychat, outbound_attempts) explicitly include ManyChat.
//     Dividing line: rates measure human setter behavior; volumes measure pipeline
//     throughput. (engagementRate + outboundNewDMs DELETED 2026-08-18, R61/R62.)
// =============================================================================

export interface ChatMessage {
  sender: "lead" | "setter" | "system";
  text: string;
}

export interface ChatExample {
  label: string;
  contact: string;
  messages: ChatMessage[];
}

export interface MetricMeta {
  /** Optional human-readable label override (falls back to the key name if absent) */
  label?: string;
  formula: string;
  columns: string[];
  filterDescription: string;
  /** Optional chat examples for DM metrics */
  chatExamples?: ChatExample[];
  /**
   * Optional flag for metrics whose definition is known to be imprecise.
   * - "inaccurate-deferred": Mako has flagged this as wrong but the fix is parked
   *   (e.g. AOV denominator inflation, cashCollected combining HT+LT).
   * - "not-implemented": Definition is documented but the pipeline does not yet
   *   compute the metric per this rule (e.g. followUps under the 6h-silence rule).
   * Consumers may surface a warning badge in tooltips.
   */
  accuracyStatus?: "inaccurate-deferred" | "not-implemented";
  /** Optional source reference: SQL file / RPC name that powers this metric */
  source?: string;
  /**
   * Which direction is healthy for this metric. Drives stoplight color logic.
   *   - 'up'      : higher is better (cash, deals, conversions)
   *   - 'down'    : lower is better (cancellations, no-shows, orphan rate, losses)
   *   - 'neutral' : no good/bad signal (rescheduled, installments_count — volume only)
   * Optional so legacy entries without a value don't break compilation.
   * Consumers should treat undefined as 'up' only when necessary; prefer explicit values.
   */
  goodDirection?: "up" | "down" | "neutral";
  /**
   * Has MAKO ruled this metric's definition? Set by R43 (2026-08-10):
   * a metric is ruled WHEN HE TOUCHES IT, never in a batch. An unruled metric
   * is DATA, not a gap, and the build does not wait for it.
   * This field seeds the codegen registry's `source` column.
   *   - "ruled"        : he stated it. Canon. Propagation is authorised.
   *   - "partial"      : he engaged but did not land it. See rulingNote.
   *   - "not-relevant" : he does not want it as a stat. REMOVAL CANDIDATE.
   *   - "parked"       : his word. Too complicated now, drives no behaviour.
   *   - "deferred"     : his word, "another day". He wants it, not today.
   *   - "as-built"     : nobody stated it. The code decides this one for now.
   * SSOT for every ruling: docs/handoff/[altara]_METRIC_RPC_SSOT.md
   */
  ruling?: "ruled" | "partial" | "not-relevant" | "parked" | "deferred" | "as-built";
  /** Why, in Mako's words where possible. Required whenever ruling !== "as-built". */
  rulingNote?: string;
}

export const METRIC_META: Record<string, MetricMeta> = {
  // --- Building Blocks (every variable used in formulas MUST be defined here) ---
  SCO: {
    ruling: "as-built",
    formula: "event_type = 'sales_call_outcome'",
    columns: ["transactions.event_type"],
    filterDescription:
      "Sales Call Outcome — the event that records what happened on a closer's call. Every SCO has a status (PIF, CPP, lost, rescheduled, no_show). This is the canonical 'a call happened' signal.",
  },
  PIF: {
    ruling: "as-built",
    formula: "status = 'paid_in_full' on a sales_call_outcome",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Lead paid in full on the call. One of two win statuses. Always a show (lead was present).",
  },
  CPP: {
    ruling: "as-built",
    formula: "status = 'closed_payment_plan' on a sales_call_outcome",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Lead closed on a payment plan. One of two win statuses. Always a show (lead was present).",
  },
  wins: {
    ruling: "ruled",
    rulingNote: "R29 — PIF+CPP only",
    formula: "PIF + CPP",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Deals closed. PIF + CPP only. NEVER includes paying_installment (that's a recurring payment, not a call outcome). Used in close rate numerator and dealsClosed.",
    goodDirection: "up",
  },
  lost: {
    ruling: "as-built",
    formula: "status = 'lost' on a sales_call_outcome",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Lead was present on the call but did not buy. Counts as a show (lead was there). Counts in close rate denominator.",
    goodDirection: "down",
  },
  no_show: {
    ruling: "as-built",
    formula: "status = 'no_show' on a sales_call_outcome",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Closer hopped on the call, lead did not appear. Counts in callsTaken (closer was there) but NOT in shows (lead wasn't). This is the SCO-recorded no_show — different from orphanPreCalls which captures cases where no SCO was ever filed.",
  },
  paying_installment: {
    ruling: "as-built",
    formula: "status = 'paying_installment' on a sales_call_outcome",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Recurring payment on an existing deal. NEVER a win. NEVER a call outcome. ALWAYS included in cash_collected. This is money coming in from a previously closed deal, not a new sale.",
  },
  orphanPreCalls: {
    ruling: "ruled",
    rulingNote: "R10 — enforced",
    formula:
      "COUNT(sales_pre_call WHERE no matching sales_call_outcome exists for that lead's booking cycle)",
    columns: ["transactions.event_type", "transactions.lead_id"],
    filterDescription:
      "Pre-calls with no SCO. Setter qualified the lead, but no call outcome was ever recorded. Used in show rate denominator. Diagnostic breakdowns: flakey (lead cancelled), ghost (host cancelled), neglected (nobody cancelled, call evaporated). The breakdowns are for coaching — the formula just checks: pre-call exists, SCO doesn't. SCO gate: if an SCO exists after a cancellation, the cancel was calendar noise.",
    goodDirection: "down",
  },
  orphanRate: {
    ruling: "ruled",
    rulingNote: "sheet — orphan pre-calls / PRE-CALLS, not callsBooked",
    formula: "orphanPreCalls / callsBooked × 100",
    columns: ["transactions.event_type", "transactions.lead_id"],
    filterDescription:
      "Share of booked pre-calls that never got an SCO. High orphan rate = upstream qualifying or scheduling problem (leads ghosting before the call, hosts cancelling, calls evaporating). Diagnostic breakdowns live on the Pre-Call Compliance page (flakey / ghost / neglected).",
    goodDirection: "down",
  },
  lostPreCalls: {
    ruling: "partial",
    rulingNote: 'soft yes — "seems right altho i dont remember what it measures"',
    formula:
      "COUNT(sales_pre_call WHERE matching sales_call_outcome with event_type='sales_call_outcome' AND status='lost' exists for that lead's email within the 7-day backwards window from pc.event_date, AND no later PIF/CPP outcome supersedes it)",
    columns: [
      "transactions.event_type",
      "transactions.status",
      "transactions.email",
      "transactions.call_start_time",
      "transactions.event_date",
      "transactions.sales_program_sold",
    ],
    filterDescription:
      "Pre-calls where the closer ended up losing the lead. Setter qualified, closer hopped on, lead said no. Setter attribution comes from sales_pre_call.setter_id (post-2026-04-28 trigger enforces pre-call rule). Excludes leads who later bought (PIF or CPP supersedes lost — net result was a win). Surface in DM Analytics setter cards next to orphan chip — same Pre-Calls tile, same 4-column journey modal pattern, different chip palette (red instead of amber). The why-they-lost reason is NOT in transactions; closer notes live in Slack #post-call-notes. Modal surfaces sales_program_sold (what they were pitched) + the conversation up to and around call time as the reverse-engineering surface.",
  },
  scoNoShows: {
    ruling: "as-built",
    formula: "COUNT(sales_call_outcome WHERE status = 'no_show')",
    columns: ["transactions.event_type", "transactions.status"],
    filterDescription:
      "SCO-recorded no-shows. Closer marked the lead as absent. Different from orphanPreCalls — these have an SCO, just with no_show status. Both feed the show rate denominator.",
    goodDirection: "down",
  },
  engaged: {
    ruling: "as-built",
    formula: "COUNT(conversations WHERE a HUMAN setter sent at least 1 message)",
    columns: ["daily_dm_metrics.engaged"],
    filterDescription:
      "Displayed as 'Replied' on the DM Health tab (label renamed from 'Engaged' Jul 22 2026 so the word stops implying a two-way exchange — the metric is unchanged). Human-setter touched. Counts every conversation where a human setter (sender_type='setter' AND ghl_sender_user_id IS NOT NULL) sent at least 1 message — lead-must-message requirement removed (WK17 review, 2026-04-26). ManyChat does NOT count (NULL ghl_sender_user_id). AND assigned to a setter in GHL (`ghl_assigned_user_id IS NOT NULL`) — unassigned conversations are excluded because setters can't see them in their GHL inbox, so they wouldn't show up in DM Analytics either. GHL's Split-Leads workflow re-triggers on every inbound message when `assignedTo` is empty, so unassigned conversations self-heal on the lead's next reply. Used in the conversationRate formula (engagementRate deleted 2026-08-18, R61).",
    goodDirection: "up",
  },
  unengagedPerSetter: {
    ruling: "as-built",
    label: "Unengaged by Setter",
    formula:
      "For each ghl_assigned_user_id (NULL excluded since S24 Apr 30 — setters can't see unassigned in GHL inbox): COUNT FILTER (is_engaged = TRUE) as engaged, COUNT FILTER (NOT is_engaged) as unengaged. Pool matches NEW DMS hero exactly.",
    filterDescription:
      "Pool: ghl_conversations where first_msg_date in date range, first_lead_msg_date IS NOT NULL, channel_type = 'instagram'. Matches total_new_dms_lead_replied filter exactly (no dismiss_type filter). Conversations with NULL `ghl_assigned_user_id` are excluded — setters can't see unassigned conversations in their GHL inbox, so they wouldn't show up in DM Analytics either. GHL's Split-Leads workflow re-triggers on every inbound message when `assignedTo` is empty, so unassigned conversations self-heal on the lead's next reply.",
    columns: [
      "ghl_conversations.ghl_assigned_user_id",
      "ghl_conversations.is_engaged",
      "ghl_conversations.first_msg_date",
      "ghl_conversations.first_lead_msg_date",
    ],
    source: "get_engaged_per_setter RPC (sql/get_engaged_per_setter.sql)",
  },

  // --- Financial ---
  cashCollected: {
    ruling: "as-built",
    formula: "SUM(cash_collected) across all transactions in date range",
    columns: ["transactions.cash_collected"],
    filterDescription:
      "All transactions with event_type IN (sales_call_outcome, low_ticket_purchase, refund). Low-ticket refunds (event_type=refund, negative cash_collected) net OUT of the low-ticket cash total; high-ticket is unaffected. [INACCURATE — DEFERRED] Combined high-ticket + low-ticket. Should be split — low-ticket distorts averages and ratios. Use highTicket.cash / lowTicket.cash for clean splits. Combined entry kept until consumers migrate.",
    accuracyStatus: "inaccurate-deferred",
    goodDirection: "up",
  },
  revenueClosed: {
    ruling: "as-built",
    formula: "SUM(revenue_closed) across all transactions in date range",
    columns: ["transactions.revenue_closed"],
    filterDescription:
      "All transactions with event_type IN (sales_call_outcome, low_ticket_purchase, refund). Refund rows carry revenue_closed=0, so revenue is unchanged by the refund netting. [INACCURATE — DEFERRED] Combined high-ticket + low-ticket. Should be split — low-ticket distorts averages and ratios. Use highTicket.revenue / lowTicket.revenue for clean splits. Combined entry kept until consumers migrate.",
    accuracyStatus: "inaccurate-deferred",
    goodDirection: "up",
  },
  aov: {
    ruling: "ruled",
    rulingNote:
      'R63, Mako 2026-09-15 — $2,000 CASH FLOOR (payment-plan refinement). HIGH-TICKET = a closed_payment_plan (ANY deposit) OR a paid_in_full with cash_collected >= $2,000. LOW-TICKET DOWNSELL = a paid_in_full with cash_collected < $2,000 (a cheaper product paid in full, e.g. Course Only). A payment plan is ALWAYS high-ticket by structure: its cash_collected is a DEPOSIT on a bigger deal, not the deal value (verified live — 137 of 150 sub-$2k CPPs are $4-5k Full Mentorship plans, so counting the deposit falsely demoted them). Field is cash_collected NOT revenue_closed. $2,000 exactly stays high-ticket ($1999-and-below is the downsell band). SEPARATION not deletion: a downsell stays in cashCollected and stays a win, NEVER a loss. Verbatim: "a payment plan is always high ticket makes a ton of sense" + "$2,000 is the number i don\'t accept and i won\'t accept it in other offers as well." Close-rate/leaderboard/commission = SEPARATE parked ruling; only the reward-not-punish principle is locked. Plan: exhibit aov-2000-floor.',
    formula:
      "SUM(cash_collected) / COUNT(deals) over HIGH-TICKET wins = status='closed_payment_plan' OR (status='paid_in_full' AND cash_collected >= 2000). A paid_in_full under $2,000 is a low-ticket downsell, excluded.",
    columns: ["transactions.cash_collected", "transactions.status", "transactions.event_type"],
    filterDescription:
      "Avg Cash per HIGH-TICKET Close. High-ticket = a payment plan (closed_payment_plan, any deposit) OR a paid-in-full sale with cash_collected >= $2,000 (R63, 2026-09-15). A paid-in-full sale under $2,000 is a low-ticket DOWNSELL (a cheaper product, e.g. Course Only): excluded from AOV + high_ticket_deals, kept in cashCollected, never a loss. A payment plan is always high-ticket because its cash_collected is a deposit on a bigger deal, not the price. cashCollected keeps every dollar. Live ht_cash is PIF+CPP cash only (no paying_installment).",
    goodDirection: "up",
  },
  installmentsCollected: {
    ruling: "as-built",
    formula: "SUM(cash_collected) WHERE status = 'paying_installment'",
    columns: ["transactions.cash_collected", "transactions.status"],
    filterDescription:
      "sales_call_outcome WHERE status = 'paying_installment'. WK17 review verified paying_installment is the only status containing 'installment' in production (127 rows) — exact match replaces the legacy LIKE filter.",
    goodDirection: "up",
  },
  installmentsCount: {
    ruling: "partial",
    rulingNote: 'soft yes — "sounds about right"',
    formula: "COUNT(*) WHERE status = 'paying_installment'",
    columns: ["transactions.status"],
    filterDescription:
      "sales_call_outcome WHERE status = 'paying_installment'. WK17 review: exact match replaces legacy LIKE filter.",
    goodDirection: "neutral",
  },
  revenueToCashRatio: {
    ruling: "as-built",
    formula: "SUM(revenue_closed) / SUM(cash_collected)",
    columns: ["transactions.revenue_closed", "transactions.cash_collected"],
    filterDescription: "All transactions in date range",
  },
  slippageRate: {
    ruling: "as-built",
    formula: "PARKED — (SUM(revenue_closed) - SUM(cash_collected)) / SUM(revenue_closed) * 100",
    columns: ["transactions.revenue_closed", "transactions.cash_collected"],
    filterDescription:
      "PARKED. Revenue-to-cash gap rate. (V2 ruling: parked, not deleted) — metric needs redesign once aov denominator is locked.",
  },

  // --- Call Metrics (S35 audit: all formulas verified) ---
  callsBooked: {
    ruling: "ruled",
    rulingNote:
      "R9 — SPLIT. qualified_calls_booked = has a pre-call — grain fixed to distinct leads 2026-08-15 (R9: setter+dialer on one lead = one)",
    formula: "COUNT(DISTINCT lead_id) WHERE event_type = 'sales_pre_call'",
    columns: ["transactions.event_type"],
    filterDescription:
      "Pre-call forms submitted by setter. A valid booking requires a pre-call. Raw call_booked events from Calendly are noise — they don't confirm setter qualification. Use sales_pre_call as the canonical booking signal. Fixed Apr 8: get_operations_kpis.calls_booked now uses sales_pre_call.",
    goodDirection: "up",
  },
  callsTaken: {
    ruling: "ruled",
    rulingNote: "R3 — shows+no_shows, all five. No 3-status version",
    formula:
      "PIF + CPP + Lost + Rescheduled + Pending Close + No-Show (all calls closers hopped on for)",
    columns: ["transactions.event_type", "transactions.status"],
    filterDescription:
      "sales_call_outcome WHERE status IN (paid_in_full, closed_payment_plan, lost, rescheduled, no_show, pending_close). Includes no-shows because the closer was on the call waiting. Excludes paying_installment (recurring payment, not a call outcome). pending_close (Pending Close, Jun 2026) counts here the moment it is filed — the call happened. It is NEVER a win; cash/revenue stay $0 until conversion.",
    goodDirection: "up",
  },
  shows: {
    ruling: "ruled",
    rulingNote: "R1/R2/R15 — PIF+CPP+lost+rescheduled+pending_close, 5 statuses",
    formula:
      "PIF + CPP + Lost + Rescheduled + Pending Close (calls where the lead was actually present). Every rescheduled SCO counts, no supersession",
    columns: [
      "transactions.event_type",
      "transactions.status",
      "transactions.lead_id",
      "transactions.call_start_time",
    ],
    filterDescription:
      "sales_call_outcome WHERE status IN (paid_in_full, closed_payment_plan, lost, rescheduled, pending_close). Rescheduled counts as a show — the lead was present, the call got pushed. Excludes no_show (lead never arrived). pending_close (Pending Close, Jun 2026) counts here the moment it is filed — the call happened. It is NEVER a win; cash/revenue stay $0 until conversion. NO Reschedule Supersession (Mako ruling 2026-10-13, card WS2-RESCHED option A: keep the live rule). Every 'rescheduled' SCO counts as a show, even when a later SCO exists for the same lead_id. History: supersession was proposed 2026-05-03, shipped to the rep portal 2026-06-02 (4c75e02) and REVERTED the same day (b7efc934) because the rep portal disagreed with the leaderboard and ops page. It was never built anywhere else. No production RPC, hook or report applies it; only a what-if probe in sql/probes mentions it. Do not add it to any RPC, hook or report script unless Mako re-rules.",
    goodDirection: "up",
  },
  noShows: {
    ruling: "ruled",
    rulingNote: "sheet — no matching SCO, no open pending, no future call_booked",
    formula:
      "SCO no_show status + orphan pre-calls (sales_pre_call with no matching sales_call_outcome)",
    columns: ["transactions.event_type", "transactions.status", "transactions.lead_id"],
    filterDescription:
      "Two components: (1) SCO no_show — lead simply didn't show up, closer marked it. (2) Orphan pre-calls — setter filed a pre-call but no sales_call_outcome ever recorded. Orphan pre-calls have diagnostic breakdowns (flakey/ghost/neglected) based on cancellation events, but the cancellation taxonomy is noise — the formula just checks: pre-call exists, SCO doesn't. SCO gate: if an SCO exists after a cancellation, the cancel was calendar noise (Calendly reschedule), not a real no-show.",
    goodDirection: "down",
  },
  rescheduled: {
    ruling: "ruled",
    rulingNote: "R15 — IS a show, is NOT a win",
    formula: "COUNT(*) WHERE event_type = 'sales_call_outcome' AND status = 'rescheduled'",
    columns: ["transactions.event_type", "transactions.status"],
    filterDescription: "Calls that were rescheduled to a different date.",
    goodDirection: "neutral",
  },
  cancellations: {
    ruling: "as-built",
    formula:
      "COUNT(*) WHERE event_type = 'call_cancelled' AND lead has a sales_pre_call AND lead has NO sales_call_outcome ever (lifetime)",
    columns: ["transactions.event_type", "transactions.lead_id", "transactions.status"],
    filterDescription:
      "Calendly cancellation events filtered down to real cancellations. Three gates: (1) pre-call exists (lead was setter-qualified, not Calendly noise), (2) lead has NO sales_call_outcome ever — past or future — so reschedule-then-close cases drop out (the cancel was just calendar noise; the real outcome trumps). The lifetime SCO check (added WK17 review, 2026-04-26) prevents counting a March cancel as a cancellation when the lead actually closed in July. For raw Calendly data, see cancellationsRaw.",
    goodDirection: "down",
  },
  cancellationsRaw: {
    ruling: "as-built",
    formula: "COUNT(*) WHERE event_type = 'call_cancelled'",
    columns: ["transactions.event_type"],
    filterDescription:
      "All Calendly cancellation events regardless of pre-call existence. Raw data — includes noise (reschedules, double-booking cleanup, unqualified leads). Use cancellations for the meaningful count.",
    goodDirection: "down",
  },
  preCallsMade: {
    ruling: "as-built",
    formula:
      "RETIRED — same as callsBooked (Apr 6 ruling collapsed these). Use callsBooked instead.",
    columns: ["transactions.event_type"],
    filterDescription:
      "RETIRED. Identical to callsBooked since the Apr 6 'callsbooked is sales pre-call' ruling. WK17 review (2026-04-26): kept as a reference entry while 21 codebase refs (6 live in useOperationsAnalytics.ts) are pending migration to the canonical callsBooked key. Do not introduce new consumers.",
  },
  callsBookedRaw: {
    ruling: "ruled",
    rulingNote: "R9 — raw_calls_booked = the Calendly number",
    formula: "COUNT(*) WHERE event_type = 'call_booked'",
    columns: ["transactions.event_type"],
    filterDescription:
      "All Calendly call_booked events. Raw data — includes unqualified leads, duplicate bookings, and noise. The meaningful 'calls booked' metric is callsBooked (= sales_pre_call count). This exists so we can still see raw Calendly volume.",
  },
  showUpRate: {
    ruling: "ruled",
    rulingNote: "R14 — shows/(shows+no_shows+orphan_precalls), his May 13 lock",
    formula: "Shows / (Shows + SCO no_shows + orphan pre-calls) × 100",
    columns: ["transactions.event_type", "transactions.status", "transactions.lead_id"],
    filterDescription:
      "No-Shows = SCO no_show status + orphan pre-calls (sales_pre_call with no matching sales_call_outcome). Deliberately bypasses flakey/ghost/neglected cancellation taxonomy — cancellation events are noise (~35% inflation without SCO gate). Orphan pre-calls capture all cases where a setter qualified a lead but no call outcome was ever recorded. Pre-call is the line: once the setter qualifies a lead, they've consumed team resources. Can never exceed 100%.",
    goodDirection: "up",
  },
  tangibleShowRate: {
    ruling: "ruled",
    rulingNote:
      "R65 — per PERSON, qualified only, same-day moves = no-show, same-day cancels excluded. Mako 2026-10-01. Separate from showUpRate (R14), does not replace it",
    formula:
      "Qualified people who took a call / (those + qualified people who never showed after a no-show or same-day move) × 100",
    columns: [
      "transactions.event_type",
      "transactions.status",
      "transactions.lead_id",
      "transactions.call_id",
      "transactions.call_start_time",
      "transactions.event_date",
    ],
    filterDescription:
      "How real people behave, not how calls are logged. GRAIN = person (lead_id), once per window: moved 5 times = 1 no-show. POPULATION = leads with a Discovery call_booked or sales_call_outcome dated in the window (org tz call date) AND a sales_pre_call (qualified). SHOWED = any SCO in the window with status lost / paid_in_full / closed_payment_plan / pending_close / unique. NEVER SHOWED = no showed SCO, but an SCO no_show, an SCO rescheduled, or a same-day Calendly move (call_cancelled on the call date or after start, matched by call_id, with a later booking for the lead). EXCLUDED: same-day cancel with no rebook (the lead was found unqualified, Mako: 'they cancel out'); cancels a day or more ahead; leads whose live rebooking is after the window (right-censored, pending); live bookings with no SCO. Differs from showUpRate on purpose: showUpRate counts shows per CALL and orphans per PERSON, and its orphans include early cancels. Rayston, Adrian, Sep 17-29 2026: 70% (46 of 66) vs showUpRate 58%. Reference SQL: docs/handoff/_artifacts/kpi-alert-system/tangible-show-rate-leadday.sql. Not on any page or RPC yet.",
    goodDirection: "up",
  },
  closeRate: {
    ruling: "ruled",
    rulingNote:
      "R21/R24/R29/R30 — final_close_rate=(PIF+CPP)/decisive_calls. decisive_calls=shows minus rescheduled. Both rates ship everywhere",
    formula:
      "TWO RATES, ALWAYS BOTH. final_close_rate = (PIF + CPP) / decisive_calls × 100. pending_close_rate = (PIF + CPP + pending_close) / decisive_calls × 100. decisive_calls = Shows − Rescheduled = PIF + CPP + Lost + Pending Close.",
    columns: [
      "transactions.status",
      "transactions.event_type",
      "transactions.lead_id",
      "transactions.call_start_time",
    ],
    filterDescription:
      "TWO close rates ship on every surface (R29/R30): rep portal, dashboard, leaderboard, client portal, no exceptions. `final_close_rate` is money that landed (PIF + CPP) and is the ONLY one prestige and the leaderboard score against. `pending_close_rate` adds provisional pending_close and is displayed beside final, NEVER scored. DENOMINATOR IS `decisive_calls`, NOT Shows (R21/R24): a reschedule is a show because the lead turned up, but it reached no decision, so it is dropped from the close-rate denominator only. Shows keeps rescheduled for SHOW rate. PROVISIONAL-WIN DOCTRINE (Mako, Jun 19 2026): pending_close lifts the pending_close_rate numerator only (via is_provisional_win = PIF | CPP | pending_close). It is NEVER counted in dealsClosed / high_ticket_deals / AOV / cash — those stay PIF + CPP. ⚠ This entry read `(PIF + CPP + pending_close) / Shows × 100` until 2026-08-11; that was the pre-R21 single-rate model and is superseded.",
    goodDirection: "up",
  },

  // --- Cash Per Call (S35: denominator = decisive shows) ---
  cashPerCallTaken: {
    ruling: "as-built",
    formula: "SUM(cash_collected) / Shows",
    columns: [
      "transactions.cash_collected",
      "transactions.status",
      "transactions.lead_id",
      "transactions.call_start_time",
    ],
    filterDescription:
      "Total cash from all sources / Shows. Shows is the canonical Shows metric (PIF + CPP + Lost + Rescheduled + Pending Close, every reschedule counted, no supersession — see the `shows` entry).",
    goodDirection: "up",
  },
  // --- Ticket Tiers ---
  "highTicket.cash": {
    ruling: "as-built",
    formula: "SUM(cash_collected) WHERE event_type = sales_call_outcome AND isNewDeal",
    columns: ["transactions.cash_collected", "transactions.status", "transactions.event_type"],
    filterDescription: "sales_call_outcome with status IN (paid_in_full, closed_payment_plan)",
  },
  "highTicket.revenue": {
    ruling: "as-built",
    formula: "SUM(revenue_closed) WHERE event_type = sales_call_outcome AND isNewDeal",
    columns: ["transactions.revenue_closed", "transactions.status", "transactions.event_type"],
    filterDescription: "sales_call_outcome with status IN (paid_in_full, closed_payment_plan)",
  },
  "lowTicket.cash": {
    ruling: "as-built",
    formula: "SUM(cash_collected) WHERE event_type = low_ticket_purchase",
    columns: ["transactions.cash_collected", "transactions.event_type"],
    filterDescription: "All low_ticket_purchase transactions",
  },
  "lowTicket.revenue": {
    ruling: "as-built",
    formula: "SUM(revenue_closed) WHERE event_type = low_ticket_purchase",
    columns: ["transactions.revenue_closed", "transactions.event_type"],
    filterDescription: "All low_ticket_purchase transactions",
  },
  "scoLowTicket.deals": {
    ruling: "ruled",
    rulingNote:
      "R64, Mako 2026-09-15 — LOW-TICKET DOWNSELL LANE. A downsell is a call-closed cheap sale: event_type=sales_call_outcome, status=paid_in_full, cash_collected < $2,000. R63 Option A means a payment plan is ALWAYS high-ticket, so CPP never enters this lane (PIF only). It stays a NORMAL SCO row — no new event_type, no new column; the $2,000 split is a query-time filter and this metric KEY is the only thing named. Distinct from lowTicket.* (event_type=low_ticket_purchase, no closer) because a downsell HAS a closer. Counts as a WIN in close rate and a show in show rate (already true — a downsell is a paid_in_full), NEVER a loss (reward-not-punish). Excluded from AOV + highTicket.deals; cashCollected keeps every dollar. Emitted by get_operations_kpis (sco_low_ticket_deals). Funnel/cohort/rep-portal propagation deferred to when a surface needs it (R45 page-trigger). Live 2026-09-15 Rayston: 56 downsell deals of 837 wins. Gaming risk flagged for future scoring ruling (a rep could pump close rate by downselling) — parked with scoring.",
    formula:
      "COUNT(*) WHERE event_type = sales_call_outcome AND status = paid_in_full AND cash_collected < 2000",
    columns: ["transactions.event_type", "transactions.status", "transactions.cash_collected"],
    filterDescription:
      "Call-closed sales (SCO) paid in full under $2,000 — the low-ticket downsell lane. Has a closer. Separate from low_ticket_purchase (automatic, no closer).",
  },
  "scoLowTicket.cash": {
    ruling: "ruled",
    rulingNote:
      "R64, Mako 2026-09-15 — see scoLowTicket.deals. Cash sum of the same downsell population (SCO paid_in_full under $2,000). Kept whole in cashCollected too; excluded from AOV.",
    formula:
      "SUM(cash_collected) WHERE event_type = sales_call_outcome AND status = paid_in_full AND cash_collected < 2000",
    columns: ["transactions.cash_collected", "transactions.event_type", "transactions.status"],
    filterDescription:
      "Cash from call-closed sales (SCO) paid in full under $2,000. The low-ticket downsell lane; also counted in cashCollected.",
  },
  ascensions: {
    ruling: "ruled",
    rulingNote: "sheet — correct as built",
    formula: "COUNT(leads who purchased low_ticket_purchase FIRST, then closed sales_call_outcome)",
    columns: [
      "transactions.event_type",
      "transactions.status",
      "transactions.event_date",
      "leads.email",
    ],
    filterDescription:
      "Leads with low_ticket_purchase before a high-ticket close (temporal sequence)",
  },

  // --- Setter / Closer (S35: uses call_booked events, not SCOs) ---
  // P12 (May 7, 2026 — Mako ruling): redefined from raw event count to distinct-lead-by-setter-role.
  setterBookedCalls: {
    ruling: "as-built",
    formula:
      "COUNT(DISTINCT lead_id) WHERE event_type='sales_pre_call' AND lead has a sales_pre_call filed by an organization_members.role='setter' member. Excludes dialer-only / closer / manager / exec / client. Multi-role pairs counted once (setter row wins via om.role='setter' allow-list).",
    columns: [
      "transactions.event_type",
      "transactions.lead_id",
      "transactions.setter_id",
      "organization_members.role",
    ],
    filterDescription:
      "True setter touch — distinct lead count where role='setter' filed pre-call. Mako May 7, 2026 ruling. Was: COUNT(*) FILTER (event_type='sales_pre_call' AND setter_id IS NOT NULL) — inflated by dialer-only leads (~3.6× on Rayston May 1-7 = 94 events vs 26 distinct setter-touched leads).",
    goodDirection: "up",
  },
  // P12 (May 7, 2026): peer to cashPerCallTaken (Shows denominator). Operations dashboard
  // uses pre_calls_filled (distinct leads from useFunnelHealth); portal uses pre_calls_made
  // (event count from get_operations_kpis) — divergence flagged P13.
  cashPerPreCall: {
    ruling: "as-built",
    formula:
      "SUM(cash_collected) / pre_calls_filled (= COUNT(DISTINCT lead_id) FILTER WHERE event_type='sales_pre_call', from get_funnel_metrics RPC). Operations dashboard uses this distinct-lead denominator (matches the PRE-CALLS card on bento). Portal currently uses get_operations_kpis.pre_calls_made (event count) — flagged for P13 unification.",
    columns: ["transactions.cash_collected", "transactions.event_type", "transactions.lead_id"],
    filterDescription:
      "Cash per distinct pre-call lead. Operations dashboard denominator = pre_calls_filled (distinct leads from useFunnelHealth). Portal denominator = pre_calls_made (event count from get_operations_kpis); divergence flagged P13. Peer to cashPerCallTaken (Shows denominator). Mako May 7, 2026.",
    goodDirection: "up",
  },
  closerBookedCalls: {
    ruling: "as-built",
    formula:
      "COUNT(call_booked events) for leads who also have a sales_pre_call (lifetime). Closer attribution: when an SCO exists for the lead, use the SCO's closer_id; when no SCO exists (cancellation), use call_booked.closer_id directly.",
    columns: [
      "transactions.event_type",
      "transactions.closer_id",
      "transactions.lead_id",
      "transactions.status",
    ],
    filterDescription:
      "Calls booked under a closer. Rule (WK17 review, 2026-04-26): start from call_booked events, gate to leads that also have a sales_pre_call (any pre-call ever counts — pre-call presence is what makes the booking 'real', not Calendly noise). For closer attribution, the SCO's closer_id wins when an SCO exists (resolves multi-closer ambiguity from reschedule transfers). When no SCO exists (cancellation subset), fall back to the call_booked.closer_id directly. The cancellation subset includes ALL failure modes: host-cancelled (Ghost), lead-cancelled (Flakey), and neglected (no cancel + no SCO).",
  },
  // Wave 17 (Stoplights org CALLS section, 2026-06-19): dialer-OWNED bookings.
  dialerBooked: {
    ruling: "ruled",
    rulingNote: "sheet — correct as built",
    formula:
      "COUNT(DISTINCT lead_id) WHERE event_type='sales_pre_call' AND filer is organization_members.role='dialer' AND NO setter filed a sales_pre_call on the same lead (lifetime).",
    columns: [
      "transactions.event_type",
      "transactions.lead_id",
      "transactions.setter_id",
      "organization_members.role",
    ],
    filterDescription:
      "Distinct dialer-OWNED leads — a dialer filed the pre-call and no setter ever did. setter-wins anti-join per /precall, so dialer-on-setter-lead assist pre-calls drop out (NOT raw dialer pre-calls). Peer to setterBookedCalls. Rayston wk 6/7: 39 owned vs 76 incl-assist (37 assist drop).",
    goodDirection: "up",
  },
  setterQualityScore: {
    ruling: "as-built",
    formula: "PARKED — AVG(custom_data->quality_of_lead) across sales_call_outcomes",
    columns: ["transactions.custom_data"],
    filterDescription:
      "PARKED. Transactions where custom_data.quality_of_lead is set. Parking rationale: needs redesign.",
  },
  conversationDepth: {
    ruling: "as-built",
    formula: "PARKED — AVG(avgConversationDepth) across sales_call_outcomes",
    columns: ["transactions.custom_data"],
    filterDescription: "PARKED. Transactions where avgConversationDepth is set.",
  },
  avgResponseTime: {
    ruling: "as-built",
    formula: "PARKED — AVG(avgResponseTimeSeconds) / 60 (converted to minutes)",
    columns: ["transactions.custom_data"],
    filterDescription: "PARKED. Transactions where avgResponseTimeSeconds is set.",
  },

  // --- Dialing (from daily_leaderboard) ---
  dials: {
    ruling: "as-built",
    formula: "SUM(daily_dials) from daily_leaderboard",
    columns: ["daily_leaderboard.daily_dials"],
    filterDescription: "All leaderboard entries in date range",
  },
  dialsPerDay: {
    ruling: "ruled",
    rulingNote: "sheet — ATTEMPTS not dials. 2 dials in 5 min, no pickup = 1",
    formula: "SUM(daily_dials) / COUNT(DISTINCT date)",
    columns: ["daily_leaderboard.daily_dials", "daily_leaderboard.date"],
    filterDescription: "Total dials divided by number of unique dates",
  },
  dialsPerConvo: {
    ruling: "partial",
    rulingNote: "attempts-not-dials ruled. Blocked on US: no dial-attempt metric exists yet",
    formula: "SUM(daily_dials) / conversations (daily exchanges)",
    columns: ["daily_leaderboard.daily_dials", "ghl_messages.sender_type"],
    filterDescription:
      "Total dials divided by conversation-days (days where both lead+setter messaged)",
  },
  dialsConvoPerCall: {
    ruling: "as-built",
    formula: "conversations / COUNT(sales_call_outcome WHERE setter_id IS NOT NULL)",
    columns: ["daily_dm_metrics.real_convos", "transactions.event_type", "transactions.setter_id"],
    filterDescription:
      "Deep conversations (4+ setter reply turns) divided by setter-attributed call outcomes. Measures how many deep DM conversations it takes per setter-sourced call. Higher = more conversations needed per booking.",
  },

  // --- Dial Analytics (dial.* namespace — from daily_dial_metrics + dial_dials) ---
  // Live GHL call-sync pipe (2-min cron), NOT the dead daily_leaderboard.daily_dials.
  // Course KPIs: Dynamis Dialing.md DIALER KPIs (~line 918).
  "dial.dials": {
    ruling: "as-built",
    label: "Dials",
    formula: "COUNT(*) of dial_dials in range (org-tz day-bucketed via daily_dial_metrics)",
    columns: ["daily_dial_metrics.total_dials"],
    filterDescription:
      "Every dial GHL logged (all statuses, inbound + outbound). Course weekly minimum 900 = 180/day × 5.",
    goodDirection: "up",
  },
  "dial.connectRate": {
    ruling: "as-built",
    label: "Connect Rate",
    formula:
      "real_connects ÷ total dials; real_connect = status IN ('completed','answered') AND duration_sec >= 45",
    columns: ["daily_dial_metrics.real_connects", "daily_dial_metrics.total_dials"],
    filterDescription:
      "Share of dials that actually REACHED a human. GHL `completed` includes 2-second hangups and machine answers (G112), so the raw completed/answered count reads high; a real connect needs 45 seconds or more of call. NULL-duration completed calls and voicemail do NOT count (a recording alone is not a connect — voicemail leaves one). Same 45s bar the Dial-Due tab uses for 'contacted'. Diagnostic — varies by source, no course target. (Mako Q14 ruling 2026-07-04, floor raised to 45s by his 2026-08-09 FINAL ruling: under 45s is voicemail and is NOT a connect. The raw pickup count is still visible in the Dial Health status mix as 'Completed'.)",
    goodDirection: "up",
  },
  "dial.conversationRate": {
    ruling: "as-built",
    label: "Connect-to-Conversation Rate",
    formula:
      "connected calls with duration_sec >= 180 ÷ real_connects (45s+); connected calls with NULL duration count as neither a real connect nor a conversation",
    columns: ["daily_dial_metrics.conversations", "daily_dial_metrics.real_connects"],
    filterDescription:
      "A conversation = a connected call of 3+ minutes (course: a real convo is 3-4+ min; we take the 3-min floor). Denominator is real_connects (45s+ reached-a-human), consistent with Connect Rate. NULL-duration calls are excluded from both. Course target 70% minimum.",
    goodDirection: "up",
  },
  "dial.attemptsPerLead": {
    ruling: "as-built",
    label: "Attempts per Lead",
    formula:
      "dials ÷ leads_touched (leads_touched = range-distinct conversation_id from raw dial_dials)",
    columns: ["dial_dials.conversation_id", "daily_dial_metrics.total_dials"],
    filterDescription:
      "How many times a dialer rings each lead on average in range. Neutral — cadence-dependent, not a good/bad signal by itself.",
    goodDirection: "neutral",
  },
  "dial.connectToBook": {
    ruling: "ruled",
    rulingNote:
      'R54 (2026-08-17): R17 definition RULED everywhere — Mako verbatim "R17 absolutely. they need to book a call while on the dial window. absolutely." = a call_booked whose event_date lands within [dial start, dial end + 15min]. Fresh 30-day measure (2026-08-17, dial_dials table): 130 window-bookings on 17,662 dials, 97 distinct leads. The as-built lb formula (calls_booked/daily_dials) is KILLED by this ruling. Compute/writer ships with the dial-analytics rebuild (DIALER_TRACKER_ULTIMATE).',
    // DEPRECATED as a displayed RATE 2026-07-04 (Mako Q14: "connect to book is
    // did they connect and book during the call. kinda weird honestly. just
    // connect rate"). The scorecard now shows `booked` as a plain COUNT and
    // Connect Rate as the headline. Definition kept for reference/history; not
    // rendered. (Distinct from lb.connectToBookRate, the leaderboard metric.)
    label: "Connect-to-Book Rate (deprecated)",
    formula:
      "R17: a call_booked transaction whose event_date lands within [dial start, dial end + 15 minutes] — the lead booked WHILE on the dial, not merely afterward. Compute/writer not yet shipped; lands with the dial-analytics rebuild.",
    columns: ["transactions.event_type", "transactions.event_date", "dial_dials.lead_id"],
    filterDescription:
      "DEPRECATED 2026-07-04 — replaced on the dialer scorecard by Connect Rate. Kept for history: booked ÷ connected, event_date (booking creation), NOT call_start_time. Course target 5-10%.",
    goodDirection: "up",
  },
  "dial.speedToLead": {
    ruling: "as-built",
    label: "Speed to Lead",
    formula:
      "% of in-range arrivals dialed within 5 minutes; arrival = MIN in-range transactions.event_date where event_type IN ('typeform_submission','webinar_signup'); dialed = first linked dial with started_at >= arrival",
    columns: [
      "transactions.event_date",
      "transactions.event_type",
      "dial_dials.started_at",
      "dial_dials.lead_id",
    ],
    filterDescription:
      "Org-level v1 (per-dialer attribution is ambiguous — whoever is on shift). Per-arrival semantics (a lead re-arriving via a new funnel starts a fresh clock). Course target 80% within 5 min / 90%+ elite.",
    goodDirection: "up",
  },
  "dial.nextDue": {
    ruling: "as-built",
    label: "Next Due",
    formula:
      "arrival anchor (MAX event_date per lead over typeform_submission/webinar_signup in last 24h) + beat offset [0,5,25,145,165,240] minutes at index attempts+1, clamped forward into the 06:00-22:00 org-tz coverage window",
    columns: ["transactions.event_date", "dial_dials.started_at"],
    filterDescription:
      "When the next cadence call for a lead is due. Beat 1 is due the moment the lead arrives — that IS the 5-minute speed-to-lead race. Due times landing outside dialer coverage hours roll forward to the next 6 AM.",
    goodDirection: "neutral",
  },
  "dial.overdueNow": {
    ruling: "as-built",
    label: "Overdue Now",
    formula:
      "active-cadence leads whose clamped next_due < now(); active = anchor in last 24h, <6 attempts, not booked (call_booked with event_date >= anchor - 5 min), not contacted (no post-anchor dial with status IN ('completed','answered') AND duration_sec > 15)",
    columns: [
      "transactions.event_date",
      "dial_dials.started_at",
      "dial_dials.status",
      "dial_dials.duration_sec",
    ],
    filterDescription:
      "Leads whose next cadence call is past due right now. Booked and contacted (45s+ connect) leads leave the cadence; a 2-second hangup does NOT count as contact.",
    goodDirection: "down",
  },
  "dial.neverDialed": {
    ruling: "as-built",
    label: "Never Dialed",
    formula: "leads whose anchor arrival is 24h-7d old with ZERO post-anchor dials and no booking",
    columns: ["transactions.event_date", "dial_dials.lead_id"],
    filterDescription:
      "Leads that arrived 1-7 days ago and have not been dialed since arriving. Excludes the last 24 hours — those leads are still in the live due-queue.",
    goodDirection: "down",
  },
  "dial.noContact": {
    ruling: "as-built",
    label: "No Contact Yet",
    formula:
      "leads whose anchor arrival is 24h-7d old with >=1 post-anchor dial, none contacted (45s+ connect), no booking",
    columns: ["dial_dials.status", "dial_dials.duration_sec", "dial_dials.lead_id"],
    filterDescription:
      "Leads that were dialed after arriving but never reached — no connected call over 15 seconds, and no booking. The dial happened; the conversation didn't.",
    goodDirection: "down",
  },
  // The 5 below were rendered on the Dial pages with NO definition anywhere until
  // 2026-08-04 (phase 0 of the Dial Analytics architecture build). Every formula
  // here is transcribed from the live SQL, not restated from the UI label:
  // daily_dial_metrics column COMMENTs (`sql/migrations/2026-07-03_daily_dial_metrics.sql:78-80`)
  // and the scorecard RPC (`sql/get_dialer_scorecards.sql:79-82,132-138,151-161`).
  "dial.talkTime": {
    ruling: "as-built",
    label: "Talk Time",
    formula: "SUM(COALESCE(duration_sec, 0)) over CONNECTED dials only",
    columns: ["daily_dial_metrics.talk_time_sec"],
    filterDescription:
      "Total time on connected calls. ⚠️ The denominator here is `connected` (status IN ('completed','answered')), NOT the 45s+ `real_connects` tier that Connect Rate uses — so 2-second hangups and machine answers DO contribute their seconds to this total, while contributing 0 to Connect Rate. NULL-duration connected rows add 0 seconds but still count as connected. This is call duration, not dialer working hours; there is no dial-time or idle-time clock in the schema (see the Activity Hours gap).",
    goodDirection: "up",
  },
  "dial.activeDays": {
    ruling: "as-built",
    label: "Active Days",
    formula: "COUNT of daily_dial_metrics day-rows in range WHERE total_dials > 0",
    columns: ["daily_dial_metrics.total_dials", "daily_dial_metrics.date"],
    filterDescription:
      "Days in the selected range on which this dialer logged at least one dial, bucketed by org-timezone calendar day. A day with zero dials has NO row at all (the refresh deletes empty slices rather than storing zeros), so this counts rows present, never rows non-zero.",
    goodDirection: "neutral",
  },
  "dial.dialsPerActiveDay": {
    ruling: "as-built",
    label: "Dials / Active Day",
    formula: "ROUND(dials ÷ NULLIF(active_days, 0), 1)",
    columns: ["daily_dial_metrics.total_dials", "daily_dial_metrics.date"],
    filterDescription:
      "Average dial volume on days this dialer actually worked. Deliberately divides by ACTIVE days, not calendar days in range — a dialer off for three days is not penalised. Compare against the course Dial Ladder (200/day at 0-2 sets, 150 at 3-4, 100 at 5+), which is a per-working-day target.",
    goodDirection: "up",
  },
  "dial.booked": {
    ruling: "as-built",
    label: "Booked",
    formula:
      "COUNT(DISTINCT lead_id) where a call_booked transaction has event_date in-range AND event_date >= that dialer's FIRST in-range dial to that lead",
    columns: ["transactions.event_type", "transactions.event_date", "dial_dials.lead_id"],
    filterDescription:
      "Distinct leads this dialer dialed in range who then booked. The first-dial fence stops a booking that predates the dial from being credited to it. A plain COUNT, not a rate — Connect-to-Book RATE was dropped 2026-07-04 (Mako Q14); see the deprecated `dial.connectToBook` entry. Uses event_date (when the booking was created), NOT call_start_time.",
    goodDirection: "up",
  },
  "dial.recordingsStored": {
    ruling: "as-built",
    label: "Recordings",
    formula: "COUNT FILTER (audio_status = 'stored')",
    columns: ["daily_dial_metrics.recordings_stored", "dial_dials.audio_status"],
    filterDescription:
      "Dials whose audio was fetched from GHL, transcoded and uploaded to the dial-recordings bucket. NOT a connect count and NOT a subset of one — voicemail leaves a recording too, so a stored recording never proves a human answered. Rows still in flight sit at 'pending'; upload failures flip to 'failed' and silently leave the queue (G109).",
    goodDirection: "neutral",
  },

  // --- Funnel ---
  dealsClosed: {
    ruling: "as-built",
    formula: "COUNT(isNewDeal from sales_call_outcome)",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription: "High-ticket wins from sales calls only (excludes low-ticket purchases)",
    goodDirection: "up",
  },
  funnelQuality: {
    ruling: "ruled",
    rulingNote:
      'R55 (2026-08-17): NAME RULED = "funnel quality" — Mako verbatim "funnel quality is good." Formula ruled earlier (deals closed / raw_calls_booked unique) but raw_calls_booked exists NOWHERE live yet — metric stays unbuildable until the raw Calendly booking count ships. Old key funnelEffectiveness renamed per R47 precedent.',
    formula: "PARKED — dealsClosed / callsBooked * 100",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "PARKED. High-ticket deals / calls booked. (V2 ruling: parked, not deleted) — callsBooked definition changed to sales_pre_call; rate recalibration needed before this metric is usable.",
  },

  // --- GHL DM Metrics ---
  // RPC: get_ghl_dm_analytics - corrected definitions
  newDMChats: {
    ruling: "ruled",
    rulingNote:
      'R4/R5/R6/R23 — a lead replying first. R47 rename EXECUTED 2026-08-17 (key was newDMs; DB column new_dms does NOT move; label "New Chats"). R6 repoint EXECUTED same day: exec dashboard + weekly stoplight read the canonical count live.',
    formula:
      "COUNT(conversations where first_msg_date IN range AND first_lead_msg_date IS NOT NULL AND ghl_assigned_user_id IS NOT NULL)",
    goodDirection: "up",
    columns: [
      "ghl_conversations.id",
      "ghl_conversations.first_msg_date",
      "ghl_conversations.first_lead_msg_date",
      "ghl_conversations.ghl_assigned_user_id",
    ],
    filterDescription:
      "Apr 30 redefinition: hero NEW DMS now = conversations born in the window where the lead has at least one message AND assigned to a setter in GHL (`ghl_assigned_user_id IS NOT NULL`). Excludes ManyChat blasts the lead never engaged with — those convos never get assigned to a setter, so counting them as 'new DMs' was misleading. Unassigned conversations are also excluded because setters can't see them in their GHL inbox, so they wouldn't show up in DM Analytics either. GHL's Split-Leads workflow re-triggers on every inbound message when `assignedTo` is empty, so unassigned conversations self-heal on the lead's next reply. The raw 'any source' count (still on daily_dm_metrics.new_dms) is shown as subtext under the hero.",
    chatExamples: [
      {
        label: "Counts (lead messaged first)",
        contact: "Mohammed Laminu",
        messages: [{ sender: "lead", text: "Yes I wanna learn more about drop shipping" }],
      },
      {
        label: "Does NOT count (setter-first, lead never replied)",
        contact: "Manual outreach",
        messages: [{ sender: "setter", text: "Hey! Saw you were interested in ecom..." }],
      },
      {
        label: "Does NOT count (ManyChat blast, lead never replied)",
        contact: "New follower",
        messages: [{ sender: "setter", text: "Hey! Appreciate you following our page..." }],
      },
    ],
  },
  // outboundNewDMs DELETED 2026-08-18 (R62). It carried ZERO signal by construction:
  // GHL never creates a conversation until ManyChat actually sends and gets a reply,
  // so conversations-created = ManyChat-sends, a forced 1:1 — an outbound count from
  // GHL can never diverge from newChats to reveal a ManyChat send-failure. Mako: "we
  // don't do that at all it's all manychat so fuck it." The DB column outbound_attempts
  // stays live (ghostRate + response denominators read it). Would only become useful
  // paired with a new-follower feed Altara does not have. See handoff Lore + memory
  // reference_manychat_ghl_no_empty_conversation.
  inboundNewDMs: {
    ruling: "partial",
    rulingNote:
      "he asked US to refresh him. Answer: new_dms minus outbound_attempts = 6,294 live. Nothing computes it",
    formula: 'COUNT(convos WHERE first_message.sender_type = "lead")',
    columns: ["ghl_conversations.id", "ghl_messages.sender_type", "ghl_messages.sent_at"],
    filterDescription:
      "Lead-first NewDMs. NEW in WK17 review (2026-04-26). Mathematically: inboundNewDMs + the setter-first complement = newDMChats (no third bucket — verified 2026-04-26 against 30,622 production conversations: 9.6% lead-first, 0.15% setter-first human, 90.3% setter-first ManyChat, 0% NULL/other). The setter-first side was named outboundNewDMs, deleted 2026-08-18 (R62).",
    chatExamples: [
      {
        label: "Inbound NewDM (lead messaged first)",
        contact: "Mohammed Laminu",
        messages: [{ sender: "lead", text: "Yes I wanna learn more about drop shipping" }],
      },
    ],
  },
  followUps: {
    ruling: "deferred",
    rulingNote: "another day",
    formula:
      "COUNT(setter messages in a conversation where each consecutive setter message is 6 hours+ apart from the prior message AND no lead reply in between)",
    columns: ["ghl_messages.sent_at", "ghl_messages.sender_type", "ghl_conversations.id"],
    filterDescription:
      "Setter persistence under prolonged lead silence. Definition (WK17 review, 2026-04-26): every setter message that is preceded by a 6h+ gap from the prior message in the conversation, with no lead reply in that gap, counts as 1 follow-up. The 6h gap applies 24/7 — no work-hours or overnight carve-outs (so 11pm setter msg → 9am setter msg next day = 1 follow-up because 10h elapsed; 10am → 4pm same day = 1 follow-up because 6h elapsed). Counting rule: 3 setter messages each 6h+ apart with no lead reply = 3 follow-ups. [INACCURATE — NOT IMPLEMENTED] Definition documented but the live pipeline does not yet compute follow-ups under this rule. Existing daily_dm_metrics.follow_ups uses a different (legacy day-gap) algorithm. Coexists with followUpStreak (live current count), followUpTurns (per-conversation), followUpTotal (aggregate).",
    accuracyStatus: "not-implemented",
    chatExamples: [
      {
        label: "Follow-up detected (outbound, no reply)",
        contact: "Ghosted lead",
        messages: [
          { sender: "setter", text: "Day 1: Hey, saw you were interested..." },
          { sender: "system", text: "(no reply)" },
          { sender: "setter", text: "Day 2: Just checking in, still interested?" },
          { sender: "system", text: "(still no reply — follow_up_count = 1)" },
        ],
      },
      {
        label: "Does NOT count (lead replied)",
        contact: "Active lead",
        messages: [
          { sender: "setter", text: "Day 1: Hey, saw you were interested..." },
          { sender: "lead", text: "Yeah tell me more" },
          { sender: "setter", text: "Day 2: Did you check the doc?" },
          { sender: "system", text: "(Not a follow-up — lead engaged)" },
        ],
      },
    ],
  },
  conversations: {
    ruling: "ruled",
    rulingNote: 'R18/sheet — 4+ lead reply turns. "deep" is retired',
    formula: "COUNT(conversations with 4+ setter reply turns)",
    columns: [
      "daily_dm_metrics.real_convos",
      "ghl_conversations.is_real_convo",
      "ghl_conversations.reply_turns",
    ],
    filterDescription:
      "Deep engagement — conversations where the setter made 4+ reply turns (setter message following a lead message). This IS the canonical conversation metric. The old 1+/1+ definition is killed. Source: daily_dm_metrics.real_convos (nightly refresh) via get_dm_analytics_fast. Date attribution: first_msg_date (when convo started). Note: setter_daily_performance.reply_turn_deep uses work_date attribution (when activity happened) — these diverge for multi-day conversations.",
    goodDirection: "up",
    chatExamples: [
      {
        label: "Counts (4+ reply turns)",
        contact: "Lihjaishkw.27",
        messages: [
          { sender: "lead", text: "metadoc" },
          { sender: "setter", text: "you seen the doc yet?" },
          { sender: "lead", text: "yeah it's 3am, starting ads tomorrow" },
          { sender: "setter", text: "Are you doing ecom or dropshipping?" },
          { sender: "lead", text: "is there a difference?" },
          { sender: "setter", text: "Yeah ecom is broader..." },
          { sender: "lead", text: "ok what about sourcing?" },
          { sender: "setter", text: "Let me send you the doc on that" },
        ],
      },
      {
        label: "Does NOT count (only 2 reply turns)",
        contact: "Quick question",
        messages: [
          { sender: "lead", text: "How much?" },
          { sender: "setter", text: "Want to chat about it?" },
          { sender: "lead", text: "nah just curious" },
          { sender: "setter", text: "No worries!" },
        ],
      },
    ],
  },
  newDMsPerDay: {
    ruling: "ruled",
    rulingNote: "sheet — correct as built",
    formula: "newDMChats / days in date range",
    columns: ["ghl_conversations.id", "ghl_conversations.first_msg_date"],
    filterDescription:
      "Average newDMChats (lead-replied + setter-assigned, per the newDMChats definition) per day.",
    chatExamples: [
      {
        label: "Example calculation",
        contact: "All Time",
        messages: [{ sender: "system", text: "3,869 DMs / 45 days = 86 DMs/day" }],
      },
    ],
  },
  dmConvosPerDay: {
    ruling: "ruled",
    rulingNote: "sheet — correct as built",
    formula: "conversations / days in date range",
    columns: ["setter_daily_performance.reply_turn_deep"],
    filterDescription:
      "Average deep conversations (4+ setter reply turns) per day. Uses first_msg_date attribution (same as `conversations`). Pairs with `dmConvosWorked` (work_date attribution) — three distinct views: Conversations Started (first_msg_date), Conversations Worked (work_date), and Sales Cycle Drift = Worked − Started.",
    chatExamples: [
      {
        label: "Example calculation",
        contact: "All Time",
        messages: [{ sender: "system", text: "1,903 convos / 45 days = 42 convos/day" }],
      },
    ],
  },
  dmConvosWorked: {
    ruling: "ruled",
    rulingNote: "sheet — correct as built",
    label: "Conversations Worked",
    formula: "COUNT(conversations setter advanced past 4 reply turns within this work_date)",
    columns: ["setter_daily_performance.reply_turn_deep"],
    filterDescription:
      "Work-date attribution: a conversation that started Apr 1 but reached 4+ turns on Apr 25 counts under Apr 25 by this rule. Pairs with `conversations` (first_msg_date attribution) which counts the same convo under Apr 1. The two numbers diverge whenever the team is grinding old leads — drift = Worked - Started.",
  },
  salesCycleDrift: {
    ruling: "as-built",
    label: "Sales Cycle Drift",
    formula: "dmConvosWorked - conversations",
    columns: ["setter_daily_performance.reply_turn_deep", "daily_dm_metrics.deep_convos"],
    filterDescription:
      "In a perfect world this is zero — conversations that started today reach depth today. Positive = team is grinding old leads (sales cycle bleeding from multi-day-old conversations). Higher positive = longer effective cycle. Coaching signal at the org and setter level.",
  },
  reply_turn_distribution: {
    ruling: "as-built",
    label: "Reply Turns",
    formula:
      "Lifetime mode: bucket conversations by COALESCE(ghl_conversations.reply_turns, 0). Window mode: bucket by COUNT(*) FILTER (WHERE setter msg immediately follows lead msg) within selected date range.",
    columns: ["ghl_conversations.reply_turns", "ghl_messages.sender_type", "ghl_messages.sent_at"],
    filterDescription:
      "Rendered on the scorecard as **Total Conversations Active** (renamed from 'Conversations assigned' Jul 8 2026 — the old label implied lead-routing volume; this actually counts conversations with in-window ACTIVITY, new + re-pinged old). The true routing-volume number is the separate 'New Conversations Assigned' tile (= speedToLead.newLeads). Conversations the setter is assigned to that had ANY in-window activity. Excludes dismissed convs unless lead messaged after dismissal. ManyChat outbound excluded (ghl_sender_user_id IS NULL filtered). On the setter scorecard, right-click the Reply Turns card to flip it in place between lifetime and window mode without opening the modal.",
  },
  qualityOfProspects: {
    ruling: "as-built",
    formula: "PARKED — (callsBooked / conversations) * 100",
    columns: ["transactions.event_type", "ghl_messages.sender_type"],
    filterDescription:
      "PARKED. Percentage of conversations that resulted in a booked call. Parking rationale: conversations definition changed to 4+ reply turns; denominator needs recalibration.",
  },
  // deepConversations: DELETED — was an alias for `conversations` with identical formula. Use `conversations` key only.

  // --- Funnel Grid Metrics (from get_funnel_metrics RPC) ---
  "funnel.webinar": {
    ruling: "as-built",
    formula: "COUNT(DISTINCT lead_id) WHERE event_type = 'webinar_signup'",
    columns: ["transactions.lead_id", "transactions.event_type"],
    filterDescription:
      "Unique people who registered for any webinar in the date range. One person at 5 webinars = 1. Renamed from funnel.registered in WK17 review (2026-04-26) for clarity.",
  },
  "funnel.applied": {
    ruling: "as-built",
    formula: "COUNT(*) WHERE event_type = 'typeform_submission'",
    columns: ["transactions.event_type"],
    filterDescription: "Typeform applications submitted. Includes both qualified and DQ'd.",
  },
  "funnel.newDMChats": {
    ruling: "ruled",
    rulingNote:
      "R6 — repoint at canonical, not just renamed. EXECUTED 2026-08-17 (key renamed from funnel.newDMs with R47; exec + stoplight repointed same wave)",
    formula:
      "COUNT(conversations WHERE first_msg_date IN range AND first_lead_msg_date IS NOT NULL AND ghl_assigned_user_id IS NOT NULL)",
    columns: [
      "ghl_conversations.id",
      "ghl_conversations.first_msg_date",
      "ghl_conversations.first_lead_msg_date",
      "ghl_conversations.ghl_assigned_user_id",
    ],
    filterDescription:
      "Identical to root `newDMChats`. R4: a New DM is a lead replying for the first time. R6 rules this key must be REPOINTED at that canonical definition, not merely renamed. ⚠ This entry read `COUNT(conversations started on this date — lead, setter, or ManyChat)` until 2026-08-11, an UNGATED all-source count with no lead-reply requirement. Measured live on Rayston for August 2026: the old ungated form returns 5,515 conversations against 1,975 canonical, a 2.79× overcount. R23 also bars the raw number from ever being a headline — it is a supporting figure only, because an unreplied conversation is unreachable.",
  },
  "funnel.newWebinar": {
    ruling: "as-built",
    formula: "COUNT(leads with exactly 1 webinar_signup ever)",
    columns: ["transactions.lead_id", "transactions.event_type"],
    filterDescription:
      "First-time webinar attendees. Uses all-time history to identify returners — if they signed up for any webinar before this period, they're not 'New'. Renamed from funnel.new in WK17 review (2026-04-26) for clarity.",
  },
  "funnel.qualified": {
    ruling: "as-built",
    formula: "COUNT(*) WHERE event_type = 'typeform_submission' AND status = 'qualified'",
    columns: ["transactions.event_type", "transactions.status"],
    filterDescription: "Typeform applicants who passed the qualification screening.",
  },
  "funnel.conversations": {
    ruling: "ruled",
    rulingNote:
      "R19 — repoint to the real conversation metric (4+ turns), kills the new_dms+real_convos sum. R18 renames the shape to `dm_conversation`. ⚠ This note cited 'R18/R19 — 4+ LEAD reply turns' until 2026-08-11; neither ruling says lead. R18 is about naming, R19 says 4+ turns without naming a side. The formula's SETTER turns is the one both /metrics and the root `conversations` key carry, so the note was the wrong side, not the formula.",
    formula:
      "COUNT(conversations WHERE reply_turns >= 4), and a reply turn is one SETTER message whose immediately preceding message came from the LEAD",
    columns: [
      "ghl_conversations.id",
      "ghl_conversations.reply_turns",
      "ghl_conversations.is_real_convo",
      "ghl_messages.sender_type",
    ],
    filterDescription:
      "Real back-and-forth engagement. ⚠ The old wording said 'setter sent 4+ replies in the conversation', which is NOT what runs. Read live from `compute_conversation_metadata` on 2026-08-11: `reply_turns` counts rows where `sender_type = 'setter'` AND `LAG(sender_type) OVER (ORDER BY sent_at) = 'lead'`. So four setter messages in a row count as ONE turn, not four. It measures alternation, not volume. Three further gates are in that same count and were never written down here: Instagram only (`channel_type = 'instagram'`), ManyChat excluded (`sender_type != 'setter' OR ghl_sender_user_id IS NOT NULL`), and system noise stripped (opportunity events, 'Contact created%', `TYPE_ACTIVITY_OPPORTUNITY` / `TYPE_INTERNAL_COMMENT` / `TYPE_CALL`). `is_real_convo` is the stored boolean `reply_turns >= 4`.",
  },
  "funnel.attended": {
    ruling: "as-built",
    formula: "COUNT(DISTINCT lead_id) WHERE webinar_signup status = 'attended'",
    columns: ["transactions.lead_id", "transactions.status"],
    filterDescription:
      "Unique people who attended at least one webinar day. Multi-day attendees counted once.",
  },
  "funnel.dqRate": {
    ruling: "as-built",
    formula: "DQ'd / (DQ'd + Qualified) × 100",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Disqualification rate from Typeform. Higher = more unqualified applicants reaching the form.",
  },
  // "funnel.deep": DELETED — exact duplicate of funnel.conversations. Use funnel.conversations only.
  "funnel.booked": {
    ruling: "as-built",
    formula: "COUNT(DISTINCT lead_id) WHERE event_type = 'call_booked' AND lead in source",
    columns: ["transactions.event_type", "lead_funnel_source.funnel_source"],
    filterDescription:
      "Leads from this source who booked a Calendly call. Uses raw call_booked events (Calendly signal), NOT sales_pre_call. Deliberately different from root callsBooked (which uses sales_pre_call as setter-qualified booking). WK17 review (2026-04-26) confirmed: funnel.booked stays on raw call_booked because the funnel grid is supposed to show ALL bookings including unqualified Calendly noise. A future funnel.preCall metric will surface canonical (sales_pre_call) per-source bookings for direct comparison.",
  },
  "funnel.preCall": {
    ruling: "as-built",
    formula: "COUNT(*) WHERE event_type = 'sales_pre_call' AND lead in source",
    columns: ["transactions.event_type", "lead_funnel_source.funnel_source"],
    filterDescription: "Setter pre-call forms submitted for leads from this source.",
  },
  "funnel.shown": {
    ruling: "as-built",
    formula: "COUNT(DISTINCT lead_id) with sales_call_outcome AND status ≠ no_show",
    columns: ["transactions.event_type", "transactions.status", "lead_funnel_source.funnel_source"],
    filterDescription: "Leads from this source who showed up to their call. Excludes no-shows.",
  },
  "funnel.closed": {
    ruling: "as-built",
    formula: "COUNT(*) WHERE status IN (paid_in_full, closed_payment_plan) AND lead in source",
    columns: ["transactions.status", "lead_funnel_source.funnel_source"],
    filterDescription: "Wins from this source. PIF + CPP deals only.",
  },
  "funnel.leadsIn": {
    ruling: "as-built",
    formula:
      "COUNT(*) FROM lead_funnel_source WHERE first_touch_at in date range, grouped by funnel_source",
    columns: ["lead_funnel_source.funnel_source", "lead_funnel_source.first_touch_at"],
    filterDescription:
      "Top of funnel — new leads attributed to each source by their first_touch_at date. Uses org timezone for date boundary.",
  },
  "funnel.showRate": {
    ruling: "ruled",
    rulingNote: "R12 — same canonical show rate",
    formula: "Shown / (Shown + SCO no_shows + orphan pre-calls) × 100",
    columns: ["transactions.event_type", "transactions.status", "lead_funnel_source.funnel_source"],
    filterDescription:
      "Per-source show rate. No-Shows = SCO no_show + orphan pre-calls (pre-call with no SCO). Bypasses flakey/ghost/neglected taxonomy — cancellation events are noise. Deliberately different from shown/booked.",
  },
  "funnel.missingPrecalls": {
    ruling: "as-built",
    formula:
      "COUNT(DISTINCT lead_id) WHERE shown (PIF+CPP+Lost) AND setter_id IS NOT NULL AND no sales_pre_call exists for that lead ever",
    columns: [
      "transactions.event_type",
      "transactions.status",
      "transactions.setter_id",
      "lead_funnel_source.funnel_source",
    ],
    filterDescription:
      "Pre-call compliance gap — leads who showed up to a call with a setter but never had a pre-call form filed. NOT date-filtered on the pre-call check (if it exists at any point, they're compliant).",
  },
  "funnel.preCallsFilled": {
    ruling: "as-built",
    formula: "COUNT(DISTINCT lead_id) WHERE event_type = 'sales_pre_call' AND lead in source",
    columns: ["transactions.event_type", "lead_funnel_source.funnel_source"],
    filterDescription: "Per-source count of leads with a sales_pre_call event in the date range.",
  },
  "funnel.totalCash": {
    ruling: "as-built",
    formula:
      "SUM(cash_collected) WHERE status IN (PIF, CPP, paying_installment) AND lead in source",
    columns: [
      "transactions.cash_collected",
      "transactions.status",
      "lead_funnel_source.funnel_source",
    ],
    filterDescription:
      "Per-source total cash including installment payments. Differs from funnel cash (PIF+CPP only) by including paying_installment recurring collections.",
  },

  // --- Additional DM metrics ---
  trueInboundDMs: {
    ruling: "as-built",
    formula:
      "RETIRED — Lead-first subset of newDMChats. Folded into newDMChats per Apr 6; newDMChats was then re-gated to lead-replied + setter-assigned on 2026-04-30 (see newDMChats).",
    columns: ["ghl_conversations.id", "ghl_messages.sender_type", "ghl_messages.sent_at"],
    filterDescription:
      "RETIRED. Was the lead-initiated slice of newDMChats. Apr 6 folded it into newDMChats; newDMChats was then re-gated on 2026-04-30 to lead-replied + setter-assigned (first_lead_msg_date IS NOT NULL AND ghl_assigned_user_id IS NOT NULL — see newDMChats). The raw all-source count lives on daily_dm_metrics.new_dms (total_new_dms). Entry kept for backward compatibility.",
  },
  realConversations: {
    ruling: "ruled",
    rulingNote: "R7 — real_convos_v2 wins, retire real_convos",
    formula:
      "RETIRED — use `conversations` key (4+ setter reply turns). This entry kept for backward compatibility only.",
    columns: ["ghl_conversations.id", "ghl_messages.sender_type"],
    filterDescription:
      "RETIRED. This metric used a 2+/2+ threshold (lead sent 2+, setter sent 2+) which does not match the canonical definition. Use `conversations` (4+ setter reply turns) instead. `deepConversations` was also deleted as a redundant alias.",
  },

  // --- DM Health Rate Metrics (computed client-side in useDMAnalytics.ts) ---
  // engagementRate DELETED 2026-08-18 (R61). Its meaningful form (deep convos /
  // newChats) is redundant with conversationRate (deep / engaged) — setters touch
  // ~99% of new chats, so the two rates collide. The live form (engaged / newChats)
  // was a ~99% vanity number. Mako: "remove engagement rate because that's already
  // taken by conversation rate."
  conversationRate: {
    ruling: "ruled",
    rulingNote:
      "sheet — RENAMES to conversationRate. EXECUTED 2026-08-17 (key was conversionRate; formula wording de-deep'd same wave)",
    formula: "Conversations / Engaged × 100",
    columns: ["daily_dm_metrics.real_convos", "daily_dm_metrics.engaged"],
    filterDescription:
      "What percentage of engaged conversations became deep (4+ setter reply turns). Measures setter ability to sustain conversations. Computed client-side.",
  },
  ghostRate: {
    ruling: "parked",
    rulingNote: "park this too complicated right now and it doesnt drive any behavior",
    formula: "Ghosted / Manual Outbound × 100",
    columns: ["daily_dm_metrics.ghosted", "daily_dm_metrics.outbound_attempts"],
    filterDescription:
      "Negligence signal — both numerator and denominator are human-setter activity only. ManyChat is excluded from both sides because automated outreach can't be 'ghosted' in the negligence sense. Volume of ManyChat-no-reply tracked separately via outbound_attempts - engaged if ever needed.",
  },

  // --- PARKED DM metrics (may return in future iterations) ---
  ghostedCount: {
    ruling: "as-built",
    formula: "PARKED — COUNT(outbound convos WHERE lead_msgs = 0)",
    columns: ["ghl_messages.sender_type", "ghl_conversations.id"],
    filterDescription:
      "PARKED. Outbound messages that got no reply. Parking rationale: metric conflates ManyChat automation with human outreach; needs redesign.",
  },
  responseRate: {
    ruling: "as-built",
    formula: "PARKED — (conversations with lead reply / total outbound attempts) * 100",
    columns: ["ghl_messages.sender_type", "ghl_conversations.id"],
    filterDescription:
      "PARKED. How often leads reply to outbound messages. Parking rationale: denominator mixes ManyChat and manual; misleading rate. NOTE: useDMAnalytics.ts computes a LIVE responseRate as (outbound - ghosted) / outbound × 100 — different formula. If unparking, reconcile with live version.",
  },
  dmResponseTime: {
    ruling: "partial",
    rulingNote: "he asked US a question: does a sleep-aware version already exist? Unanswered",
    formula: "AVG(time between lead message and setter reply)",
    columns: ["ghl_messages.sent_at", "ghl_messages.sender_type"],
    filterDescription:
      "Average minutes for setter to reply after lead message. Target: <30 min during work hours.",
  },

  // `wait` is the noun Mako ruled on Aug 6 2026 for the RAW wall-clock number.
  // It exists so that "reply time" can always mean the offline-corrected scorecard
  // number and the two never collide. Four surfaces spell it identically: the
  // Timeline row badge's aria-label, the Reply Timeline sidebar's avg/med labels,
  // the methodology modal's two headings, and this entry. A near-miss spelling is
  // worse than no noun, because it reads as two metrics.
  wait: {
    label: "Wait",
    formula: "lead's most recent message -> the setter's next message, raw wall-clock",
    columns: ["ghl_messages.sent_at", "ghl_messages.sender_type"],
    goodDirection: "down",
    filterDescription:
      "Raw wall-clock time the lead actually waited. No offline subtraction, no exclusions, no suffering filter. This is DELIBERATELY not the same number as Setter Reply Time, and the two must never be reconciled. There are FIVE reply-time numbers on the DM page and collapsing any of them destroys information: (1) Setter Reply Time (`avg_reply_time`) subtracts offline minutes and drops suffering conversations, because the file's own comment calls those 'forgetting time, not reply time'; (2) Engaged Response counts only replies inside the same work session; (3) Lead Reply Time measures the OPPOSITE direction, how fast the lead answers; (4) this metric, `wait`, on the Timeline row badge, per message, anchored on the lead's LAST message of a run; (5) the Reply Timeline sidebar's avg/med, also raw wall-clock but scoped to ONE conversation across its whole lifetime and anchored on the lead's FIRST message of a run. (4) and (5) share this noun because both are raw. They still differ in scope and anchor, which is why each carries its own parenthetical on the methodology modal. Numbers 2, 3, 4 and 5 all read HIGHER than (1); none reads lower.",
  },

  // --- V2 New Metrics (Apr 6 rulings) ---
  neglected: {
    ruling: "as-built",
    formula:
      "COUNT(leads with pre-call done + call booked + NOT cancelled + no sales_call_outcome)",
    columns: ["transactions.event_type", "transactions.status", "transactions.lead_id"],
    filterDescription:
      "Diagnostic breakdown of orphan pre-calls. Lead qualified (pre-call done), call was booked, never cancelled, but no sales_call_outcome exists. The call just evaporated. NOT a direct show rate input — show rate uses the orphan pre-call umbrella instead. Useful for coaching: 'why did this pre-call never become a call?'",
  },
  delayed: {
    ruling: "ruled",
    rulingNote: "sheet — KEEP the metric, RENAME to preCallMissed",
    formula: "COUNT(calls cancelled and rebooked to a later date because no pre-call existed)",
    columns: ["transactions.event_type", "transactions.event_date", "transactions.lead_id"],
    filterDescription:
      "Call was booked, but no pre-call was available so the closer could not take it. Call gets cancelled and rebooked for a later date. Setter/dialer accountability metric — traces which setter/dialer failed to pre-call, causing the delay.",
  },
  untouched: {
    ruling: "as-built",
    formula: "COUNT(conversations WHERE lead_msgs >= 1 AND setter_msgs = 0)",
    columns: ["ghl_conversations.id", "ghl_messages.sender_type"],
    filterDescription:
      "Lead reached out but nobody picked it up. One metric, three views: daily (what happened today), current (what needs fixing now), trend (getting better?). Not three separate metrics.",
  },
  cashPerCall: {
    ruling: "as-built",
    formula: "SUM(cash_collected) / callsBooked (settled: call_start_time before today, org tz)",
    columns: ["transactions.cash_collected", "transactions.event_type"],
    filterDescription:
      "Cash per booked call. Denominator = callsBooked (sales_pre_call count — setter-qualified bookings). Different from cashPerCallTaken (denominator = shows, calls that actually happened). cashPerCallRaw was deleted in WK17 review — Calendly-noise denominator with no consumers.",
  },
  followUpStreak: {
    ruling: "as-built",
    formula:
      "Current consecutive new-day setter messages without lead reply (resets to 0 when lead responds)",
    columns: ["ghl_conversations.follow_up_streak"],
    filterDescription:
      "Active follow-up count on a conversation. Increments when setter sends on a new day and lead hasn't replied. Resets to 0 when lead responds. Scope: current (live state per conversation).",
  },
  followUpTurns: {
    ruling: "deferred",
    rulingNote: "another day",
    formula: "COUNT(setter messages sent after lead silence of 24+ hours) per conversation",
    columns: ["ghl_messages.sent_at", "ghl_messages.sender_type"],
    filterDescription:
      "Number of follow-up attempts (setter msg after 24h+ lead silence) within a single conversation. Higher = more persistent setter.",
  },
  followUpTotal: {
    ruling: "deferred",
    rulingNote: "this one needs work for sure but another day",
    formula: "SUM(followUpTurns) across all conversations for a setter in date range",
    columns: ["ghl_messages.sent_at", "ghl_messages.sender_type", "ghl_conversations.id"],
    filterDescription:
      "Total follow-up attempts across all conversations. Aggregate of followUpTurns.",
  },

  // --- Portal Hero Card Metrics (portal.* namespace) ---
  "portal.cashCollected": {
    ruling: "as-built",
    formula: "SUM(cash_collected) from winning transactions in last 30 days",
    columns: ["transactions.cash_collected"],
    filterDescription:
      "status IN (paid_in_full, closed_payment_plan, paying_installment), last 30 days",
  },
  "portal.closeRate": {
    ruling: "ruled",
    rulingNote: "R29/R30 — both final and pending shown, gates score final",
    formula:
      "TWO RATES, ALWAYS BOTH. final_close_rate = (PIF + CPP) / decisive_calls × 100. pending_close_rate = (PIF + CPP + pending_close) / decisive_calls × 100. decisive_calls = Shows − Rescheduled.",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Last 30 days. Identical model to root `closeRate` — the client portal is NOT exempt (R29: both rates ship on every surface, client portal named explicitly). `final_close_rate` numerator = paid_in_full + closed_payment_plan. `pending_close_rate` numerator adds provisional pending_close (is_provisional_win). pending_close lifts the pending rate ONLY, NEVER dealsClosed / high_ticket_deals / AOV / cash (those stay PIF + CPP). paying_installment is NEVER a win — it is a recurring payment event, not a call outcome. DENOMINATOR = `decisive_calls` = PIF + CPP + Lost + Pending Close, i.e. Shows minus Rescheduled (R21/R24). ⚠ This entry read `(Wins + pending_close) ÷ Shows × 100` until 2026-08-11, a single rate over Shows; that is the pre-R21 model and is superseded. Its old denominator note also listed a 4-status Shows, which R1 superseded by adding pending_close.",
  },
  "portal.showRate": {
    ruling: "ruled",
    rulingNote: "R12 — same canonical show rate, no separate metric",
    formula: "Shows ÷ (Shows + SCO no_shows + orphan pre-calls) × 100",
    columns: ["transactions.status", "transactions.event_type"],
    filterDescription:
      "Last 30 days. Same formula as root showUpRate. No-Shows = SCO no_show + orphan pre-calls (pre-call with no SCO). Bypasses flakey/ghost/neglected taxonomy — cancellation events are noise. Flakey/ghost/neglected are diagnostic breakdowns only, not formula inputs.",
  },
  "portal.callsScheduled": {
    ruling: "as-built",
    rulingNote:
      'R57 (2026-08-17): future-only window RATIFIED, label = "Upcoming Calls" — Mako verbatim "it\'s useless to have an upcoming calls section that looks at calls in the past". The fn ignores its date params BY DESIGN now.',
    formula: "COUNT of booked calls with start time in the future",
    columns: ["transactions.call_start_time", "transactions.event_type"],
    filterDescription: "event_type = call_booked AND call_start_time >= NOW()",
  },
  "portal.dmActivity": {
    ruling: "as-built",
    formula: "Real conversations active in last 24 hours",
    columns: ["ghl_conversations.last_message_at", "ghl_conversations.is_real_convo"],
    filterDescription:
      "Conversations with 4+ setter reply turns AND last message was within 24h. Matches canonical deep conversation definition.",
  },
  "portal.leadsInPipeline": {
    ruling: "as-built",
    formula: "Low-ticket customers in 7-day upsell window",
    columns: ["leads.status", "leads.created_at"],
    filterDescription:
      "low_ticket_customer created within 7 days. These leads finished their course and are pending upsell. Pre-call leads excluded (already counted in Upcoming Calls).",
  },

  // --- Leaderboard Metrics (lb.* namespace) ---
  "lb.cashCollected": {
    ruling: "as-built",
    formula: "SUM(cc_total) for this rep on selected date(s)",
    columns: ["daily_leaderboard.cc_total"],
    filterDescription: "Filtered by sector + date + organization_id",
  },
  "lb.showRate": {
    ruling: "ruled",
    rulingNote: "R13 — correct as built. metricMeta was the stale side",
    formula: "Shows / (Shows + No-Shows + orphan_precalls) × 100. Identical to global showUpRate.",
    columns: [
      "daily_leaderboard.show_rate",
      "daily_leaderboard.shows",
      "daily_leaderboard.no_shows",
      "daily_leaderboard.orphan_precalls",
    ],
    filterDescription:
      "Same canonical show rate as root `showUpRate` (R14, Mako's May 13 2026 lock). Shows = PIF + CPP + Lost + Rescheduled + Pending Close (5 statuses per R1). No-Shows = SCO no_show status. orphan_precalls = pre-call with no matching SCO. R13: `LeaderboardPage.tsx:1416` was already computing the canonical form and metricMeta was the stale side, in Mako's words _\"For leaderboard show rate, the daily leaderboard is absolutely right.\"_ ⚠ This entry read `Shows / (Shows + No-Shows) × 100. Narrower than global showUpRate` until 2026-08-11 and claimed daily_leaderboard had no orphan signal. It does: the `orphan_precalls` column was added by `sql/leaderboard/add_orphan_precalls_and_fix_generated_cols.sql`. Any remaining lb-vs-root gap is STALENESS in the recalc cron, never a formula difference.",
  },
  "lb.closerCPC": {
    ruling: "as-built",
    formula: "cc_total / calls_taken (calls_taken = shows + no_shows)",
    columns: ["daily_leaderboard.cc_total", "daily_leaderboard.calls_taken"],
    filterDescription:
      "Closer sector only. calls_taken = shows + no_shows (closer hopped on, includes no-shows). NOTE: canonical CPC uses shows-only denominator — current code includes no-shows which deflates CPC.",
  },
  "lb.setterCPC": {
    ruling: "as-built",
    formula:
      "cc_total / calls_booked (when > 0), else cc_total / (shows + no_shows) (when > 0), else 0",
    columns: [
      "daily_leaderboard.cc_total",
      "daily_leaderboard.calls_booked",
      "daily_leaderboard.shows",
      "daily_leaderboard.no_shows",
    ],
    filterDescription:
      "Setter sector only. calls_booked is often 0 for setters, so we use shows + no_shows as a proxy (each outcome implies a booking).",
  },
  "lb.dialerCPC": {
    ruling: "as-built",
    formula: "cc_total / calls_taken (calls_taken = shows + no_shows)",
    columns: ["daily_leaderboard.cc_total", "daily_leaderboard.calls_taken"],
    filterDescription:
      "Dialer sector only. Same formula as closer CPC. calls_taken includes no-shows — canonical denominator is shows only.",
  },
  "lb.callsBooked": {
    ruling: "as-built",
    formula:
      "calls_booked column value — should be sales_pre_call count, not call_booked events. Display uses shows + no_shows as proxy when calls_booked is 0.",
    columns: [
      "daily_leaderboard.calls_booked",
      "daily_leaderboard.shows",
      "daily_leaderboard.no_shows",
    ],
    filterDescription:
      "Setter sector only. Canonical value = sales_pre_call count per setter per day. The calls_booked column is often 0 for setters (Calendly events lack setter attribution), so display uses shows + no_shows as a proxy.",
  },
  "lb.dailyDials": {
    ruling: "as-built",
    formula: "Total outbound dials made on selected date",
    columns: ["daily_leaderboard.daily_dials"],
    filterDescription:
      "Dialer sector only. Raw count from dialer tool integration. Canonical course target: 180/day = 900/week (5-day work week).",
  },
  "lb.connectToBookRate": {
    ruling: "as-built",
    rulingNote:
      "R54 (2026-08-17) killed this as-built formula (mixed populations: pre-call count / dials). The generated column reads daily_dials which has had NO live writer since the dialer-tracker shelving — value is 0 on every row. See dial.connectToBook for the ruled definition.",
    formula: "calls_booked / daily_dials × 100",
    columns: ["daily_leaderboard.calls_booked", "daily_leaderboard.daily_dials"],
    filterDescription:
      "Generated column in DB. Dialer sector only. Measures how many dials convert to booked calls.",
  },
  "lb.pointsEarned": {
    ruling: "as-built",
    formula: "Points from pod rank: 1st → 5pts, 2nd → 3pts, 3rd → 2pts, 4th → 1pt, 5th+ → 0",
    columns: ["daily_leaderboard.points_earned"],
    filterDescription:
      "Based on weighted rank within pod. Only top 4 earn points. Calculated nightly by calculate_rankings().",
  },
  "lb.closerRank": {
    ruling: "as-built",
    formula:
      "Pod rank by weighted score: 50% Cash Collected + 25% Show Rate + 25% CPC. Each metric normalized to [0-1] by dividing by the pod's max value.",
    columns: ["daily_leaderboard.rank", "daily_leaderboard.rank_score"],
    filterDescription:
      "Closer sector. Ranked within pod (PARTITION BY pod_id). Ties broken by weighted score.",
  },
  "lb.setterRank": {
    ruling: "as-built",
    formula:
      "Pod rank by weighted score: 40% CPC + 30% Show Rate + 30% Calls Booked. Each metric normalized to [0-1] by dividing by the pod's max value.",
    columns: ["daily_leaderboard.rank", "daily_leaderboard.rank_score"],
    filterDescription:
      "Setter sector. Ranked within pod (PARTITION BY pod_id). CPC uses calls_booked denominator (or shows+no_shows proxy).",
  },
  "lb.dialerRank": {
    ruling: "as-built",
    formula:
      "Pod rank by weighted score: 30% CPC + 25% Daily Dials + 25% CTB Rate + 20% Show Rate. Each metric normalized to [0-1] by dividing by the pod's max value.",
    columns: ["daily_leaderboard.rank", "daily_leaderboard.rank_score"],
    filterDescription:
      "Dialer sector. Ranked within pod (PARTITION BY pod_id). 4-metric composite score.",
  },
  "lb.monthlyPrestige": {
    ruling: "ruled",
    rulingNote: "R22/R26 — canonical close rate, no prestige exception",
    formula:
      "SUM of daily points_earned for this rep + sector across current calendar month. Resets on the 1st.",
    columns: ["prestige_points.total_points"],
    filterDescription:
      "Accumulated from daily_leaderboard via rollup_prestige_points(). pg_cron runs nightly at 11:55 PM PHT.",
  },
  speedToLead: {
    ruling: "as-built",
    label: "Speed to Lead",
    formula:
      "MEDIAN(minutes from a lead's first-ever inbound IG message to the setter's first human reply, offline-subtracted)",
    columns: [
      "ghl_messages.sent_at",
      "ghl_conversations.first_lead_msg_date",
      "ghl_conversations.ghl_assigned_user_id",
    ],
    filterDescription:
      "New = any assigned conversation whose lead sent their FIRST-EVER message inside the date range — bot-first conversations included (ManyChat auto-greets every new follower, so ~99% of convos open bot-first; the lead's reply to the greeting IS the new-lead moment, and bot time counts — Mako ruling Jun 10 2026). Reply must be a human setter message (sender_type='setter' AND ghl_sender_user_id IS NOT NULL — ManyChat excluded). Gaps run through subtract_offline_minutes (5h+ session gaps removed) so overnight doesn't read as negligence. Companion stat: % answered under 5 minutes. Leads under 4h old with no reply yet are 'pending', not failures. Distinct from Setter Reply Time, which averages EVERY lead→setter turn. RPC: get_setter_speed_to_lead.",
    goodDirection: "down",
  },
  prestigeKpi: {
    ruling: "deferred",
    rulingNote: "needs to compute on the go but ill do this later its a different conversation",
    label: "KPI Prestige Points",
    formula: "SUM(+1 per day the daily KPI target is hit, −1 per day it's missed) — current month",
    columns: [
      "kpi_daily_compliance.hit_kpi",
      "kpi_daily_compliance.date",
      "kpi_daily_compliance.details",
    ],
    filterDescription:
      "Every day, the daily-KPI cron scores your role against fixed targets — Closer: show rate ≥ 70% AND close rate ≥ 50%; Setter: show rate ≥ 70%; Dialer: connect-to-book ≥ 5%. Hit the target(s) that day → +1 prestige point; miss → −1. The KPI number is the running sum for the CURRENT month, so a negative value (e.g. −6) means you missed more scored days than you hit this month. The rows below are each scored day this month, newest first: the ± it earned and the metric-vs-target that decided it. Zero-activity days are not scored (no ± either way). NOTE: the KPI tile is a nightly snapshot (prestige_points), while these rows are live (kpi_daily_compliance) — a day scored since last night's roll-up shows here before it moves the tile; they re-sync each night. Source: kpi_daily_compliance, populated nightly by evaluate_daily_kpi_compliance.",
    goodDirection: "up",
  },
};

// =============================================================================
// DIAL METRIC KEYS — typed allowlist for the Dial Analytics surfaces
// =============================================================================
// WHY THIS EXISTS: METRIC_META is `Record<string, MetricMeta>`, so EVERY string
// type-checks as a valid key and a typo is invisible to tsc. The tooltip
// components then hit their `if (!meta) return null` guard, which sits ABOVE the
// JSX that wraps the visible label — so a mistyped key does not throw, it
// ERASES the label and leaves a headless number on the dashboard. There is
// already one live instance of this class in the repo (`cashPerCallBooked`,
// consumed at components/dashboard/KPIGrid.tsx and sections/ClientStats.tsx).
//
// Widening METRIC_META itself (dropping the Record annotation for `satisfies`)
// would give repo-wide safety but breaks every consumer that indexes it with a
// plain `string`. That is a separate change with real blast radius. This
// allowlist buys the same guarantee for the dial surfaces at zero risk:
// MetaTooltip accepts only DialMetricKey, so a call-site typo is a COMPILE
// error, and the dev-time assertion below catches a key that exists in the
// union but was never added to METRIC_META.
export const DIAL_METRIC_KEYS = [
  "dial.dials",
  "dial.connectRate",
  "dial.conversationRate",
  "dial.attemptsPerLead",
  "dial.speedToLead",
  "dial.nextDue",
  "dial.overdueNow",
  "dial.neverDialed",
  "dial.noContact",
  "dial.talkTime",
  "dial.activeDays",
  "dial.dialsPerActiveDay",
  "dial.booked",
  "dial.recordingsStored",
] as const;

export type DialMetricKey = (typeof DIAL_METRIC_KEYS)[number];

// Fails LOUDLY in dev the moment a key in the union has no METRIC_META entry.
// Deliberately noisy: silence is the bug this whole block exists to prevent.
// `dial.connectToBook` is intentionally absent from the union — it is the
// deprecated RATE (Mako Q14, 2026-07-04), kept in METRIC_META for history and
// never rendered.
if (import.meta.env.DEV) {
  const missing = DIAL_METRIC_KEYS.filter((k) => !METRIC_META[k]);
  if (missing.length > 0) {
    throw new Error(
      `[metricMeta] DIAL_METRIC_KEYS lists ${missing.length} key(s) with no METRIC_META entry: ${missing.join(", ")}. ` +
        `A missing entry renders as an ERASED label, not an error — add the definition before shipping.`
    );
  }
}
